
import { Vue, Options } from "vue-class-component";
import SearchFlightHeader from "./search-flight-header.vue";
import DatePicker from "../components/search-flight/date-picker.vue";
import FilterFlight from "../components/search-flight/filter-flight.vue";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import CardFlight from "../components/search-flight/card-flight.vue";
import { BagOrSttDetail, CalculateVolume, CargoV2SearchFlight, CargoV2SearchFlightResult, FormCargoV2 } from "@/domain/entities/CargoV2";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import StatusHandler from "@/app/ui/components/status-handler/index.vue";
import ModalDelayConfirmation from "../components/search-flight/modal-delay-confirmation.vue";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { Snackbar } from "@/domain/entities/MainApp";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { CalculateVolumeApiRequestV2, CalculateVolumeRequestV2, CargoV2SearchFlightRequest, RequestCheckEligibleRtcV2 } from "@/data/payload/api/CargoV2ApiRequest";
import { CheckEligible, useCargoV2 } from "../composables";
import { FlagsPermissionCargoV2 } from '@/feature-flags/flags-permission-cargo-v2';

const { onCheckEligible } = useCargoV2()

@Options({
  components: {
    SearchFlightHeader,
    DatePicker,
    FilterFlight,
    CardFlight,
    StatusHandler,
    ModalDelayConfirmation,
  },
})
class CargoV2CreateSearchFlight extends Vue {
  filterOriginAirport: string[] = [];
  filterDestinationAirport: string[] = [];
  filterLegs: number[] = [];

  isDelayModalOpen = false;
  get originAirportOptions() {
    const uniqueOrigin = new Set<string>();
    this.searchFlightResult.data.forEach((item) => {
      uniqueOrigin.add(item.originAirportCode);
    });
    return Array.from(uniqueOrigin);
  }
  get destinationAirportOptions() {
    const uniqueDestination = new Set<string>();
    this.searchFlightResult.data.forEach((item) => {
      uniqueDestination.add(item.destinationAirportCode);
    });
    return Array.from(uniqueDestination);
  }
  resetFilterAirports() {
    this.filterOriginAirport = [];
    this.filterDestinationAirport = [];
  }
  toggleDelayModal() {
    this.isDelayModalOpen = !this.isDelayModalOpen;
  }

  goBack() {
    const paramsId = (this.$route.params.id as string) || "";
    this.$router.replace(
      (this.$route.meta.before as string).replace(":id", paramsId),
    );
  }

  fetchSearchFlight() {
    CargoV2Controller.fetchSearchFlight();
  }

  get formBooking() {
    return CargoV2Controller.form;
  }

  async mounted() {
    CargoV2Controller.setLoadingSearchFlight(true);
    await CargoV2Controller.calculateVolumeForSearchFlight();

    this.searchFlightParams.originCityCode =
      AccountController.accountData.account_type_detail?.partnerLocation
        ?.city_code || "";
    this.searchFlightParams.destinationCityCode =
      this.formBooking.destinationCityCode?.code || "";
    this.searchFlightParams.originCityName = AccountController.accountData.account_type_detail?.partnerLocation
    ?.city?.name?.toLowerCase() || "";
    this.searchFlightParams.destinationCityName =
      this.formBooking.destinationCityCode?.name || "";
    this.searchFlightParams.actualTotalCargoGrossweight =
      this.formBooking.actualTotalCargoGrossweigth || 0;
    this.searchFlightParams.actualTotalCargoVolumeweight =
      this.formBooking.actualTotalCargoVolumeweight || 0;
    this.searchFlightParams.actualTotalPiece =
      this.formBooking.cargoActualPiece || this.formBooking.totalPiece || 0;
    this.searchFlightParams.commodityGroupCode =
      this.formBooking.cargoCommodity?.code || "";
    this.searchFlightParams.dimensionArray = this.formBooking.dimensions ?? [];
    this.searchFlightParams.productCargo = this.formBooking.productCargo || "";
    this.searchFlightParams.shcCode = this.formBooking.shc ?? "";
    this.searchFlightParams.isDangerousGoods =
      this.formBooking.cargoCommodity?.isDangerousGoods || false;
    this.searchFlightParams.isQuarantine =
      this.formBooking.cargoCommodity?.isQuarantine || false;
    this.searchFlightParams.rtcId = this.formBooking.rtcId || 0;

    this.fetchSearchFlight();
    if (
      !this.searchFlightParams.originCityCode ||
      !this.searchFlightParams.destinationCityCode
    ) {
      this.goBack();
    }
  }

  unmounted() {
    CargoV2Controller.setSearchFlightResult(new CargoV2SearchFlightResult());
    CargoV2Controller.setSearchFlightParams(new CargoV2SearchFlightRequest());
  }

  get searchFlightParams() {
    return CargoV2Controller.searchFlightParams;
  }

  get isLoading() {
    return CargoV2Controller.isLoadingSearchFlight;
  }

  get error() {
    return CargoV2Controller.errorCauseSearchFlight;
  }

  get isEmpty() {
    return this.filteredSearchFlightResult.length === 0;
  }

  get searchFlightErrorMessage() {
    return CargoV2Controller.searchFlightErrorMessage;
  }

  get isFlightNotFound() {
    return CargoV2Controller.isFlightNotFound;
  }

  get isFiltered() {
    return (
      this.filterOriginAirport.length ||
      this.filterDestinationAirport.length ||
      this.filterLegs.length
    );
  }

  get iconStatus() {
    if (this.error === "server") {
      return "error-token-ngen";
    }
    if (this.error === "internet") {
      return "error-internet";
    }
    return this.isFiltered ? "ill_spot_search" : "empty-flight-schedule-v2";
  }

  get headerMessage() {
    if (this.isLoading) {
      return this.$t("cargo.v2.flight.search.empty.loadingTitle");
    }
    if (this.isFlightNotFound) {
      return this.isFiltered
        ? this.$t("cargo.v2.flight.search.empty.filterNotFoundTitle")
        : this.$t("cargo.v2.flight.search.empty.noScheduleTitle");
    }
    if (this.error === "server") {
      return this.$t("cargo.v2.flight.search.empty.serverErrorTitle");
    }
    if (this.error === "internet") {
      return this.$t("cargo.v2.flight.search.empty.networkErrorTitle");
    }
    return this.isFiltered
      ? this.$t("cargo.v2.flight.search.empty.filterNotFoundTitle")
      : this.$t("cargo.v2.flight.search.empty.noScheduleTitle");
  }

  get bodyMessage() {
    if (this.isLoading) {
      return this.$t("cargo.v2.flight.search.empty.loadingMessage");
    }
    if (this.isFlightNotFound) {
      return this.isFiltered
        ? this.$t("cargo.v2.flight.search.empty.filterNotFoundMessage")
        : this.$t("cargo.v2.flight.search.empty.noScheduleMessage");
    }
    if (this.error === "server") {
      return (
        this.searchFlightErrorMessage ||
        this.$t("cargo.v2.flight.search.empty.serverErrorMessage")
      );
    }
    if (this.error === "internet") {
      return this.$t("cargo.v2.flight.search.empty.networkErrorMessage");
    }
    return this.isFiltered
      ? this.$t("cargo.v2.flight.search.empty.filterNotFoundMessage")
      : this.$t("cargo.v2.flight.search.empty.noScheduleMessage");
  }

  get searchFlightResult() {
    return CargoV2Controller.searchFlightResult;
  }

  get filteredSearchFlightResult() {
    return this.searchFlightResult.data.filter((item) => {
      const originPass = this.filterOriginAirport.length
        ? this.filterOriginAirport.includes(item.originAirportCode)
        : true;
      const destinationPass = this.filterDestinationAirport.length
        ? this.filterDestinationAirport.includes(item.destinationAirportCode)
        : true;
      const legsPass = this.filterLegs.length
        ? this.filterLegs.includes(item.transitNumber) ||
          (this.filterLegs.includes(3) && item.transitNumber >= 3) // >=3 transit: 3-n transit
        : true;
      return originPass && destinationPass && legsPass;
    });
  }

  get useFcsHistoryDepartureBadge() {
    return FlagsPermissionCargoV2.permission_history_departure_status.isEnabled();
  }

  onStartDateChange() {
    this.resetFilterAirports();
    this.fetchSearchFlight();
  }
  onFilterFlightChange(val?: string | number) {
    this.fetchSearchFlight();
  }

  selectedFlight = new CargoV2SearchFlight();

  handleSelectFlight(flight: CargoV2SearchFlight) {
    this.selectedFlight = flight;
    if (flight.historyDepartureStatus === "delayed" && this.useFcsHistoryDepartureBadge) {
      this.toggleDelayModal();
    } else {
      this.onSave(flight);
    }
  }

  goBackFromSubmit() {
    const beforeRoute = this.$route.meta.before as string;
    CargoV2Controller.setForm(new FormCargoV2());
    CargoV2Controller.setCalculateVolumeData(new CalculateVolume())
    CargoV2Controller.setUploadProofPhoto(false);
    if (beforeRoute.includes("manual")) {
      return this.$router.replace("/cargo-v2/create/manual");
    }
    if (beforeRoute.includes("rebooking")) {
      return this.$router.replace("/cargo-v2/booking-cancelled");
    }
    return this.$router.replace("/cargo-v2/create");
  }

  private _getBagOrSttForCheckEligibleFromForm(): BagOrSttDetail[] {
    const list = this.formBooking.bagOrStt ?? [];
    const result: BagOrSttDetail[] = [];
    for (const row of list) {
      if (row.bagNo) {
        result.push(new BagOrSttDetail({ bagNo: row.bagNo }));
      } else if (row.sttNo) {
        result.push(new BagOrSttDetail({ sttNo: row.sttNo }));
      }
    }
    return result;
  }

  navigateToEdit() {
    const id = String(
      (this.$route.params.id as string) || this.formBooking.rtcId || "",
    );
    const path = this.$route.path ?? "";
    if (path.includes("manual")) {
      this.$router.replace("/cargo-v2/create/manual");
    } else if (path.includes("rebooking")) {
      this.$router.replace(`/cargo-v2/${id}/rebooking`);
    } else {
      this.$router.replace(`/cargo-v2/create/rtc/${id}`);
    }
  }

  private _handleCheckEligibleSubmit(data: {
    bagOrStt: { sttNo?: string; bagNo?: string }[];
  }) {
    const nonEligibleData = data.bagOrStt.map(
          (item) => new BagOrSttDetail(item)
        );
    CargoV2Controller.onDeleteBagOrStt(nonEligibleData);
    if (this.formBooking.bagOrStt.length) {
      return this.navigateToEdit();
    }
    return this.goBackFromSubmit();
  }

  async onSave(flight: CargoV2SearchFlight) {
    this.isDelayModalOpen = false;

    const bagOrSttForCheckEligible = this._getBagOrSttForCheckEligibleFromForm();

    try {
      await onCheckEligible(
        new CheckEligible({
          bagOrStt: bagOrSttForCheckEligible,
          success: () => {
            this._submitFromSearchFlight(flight);
          },
          ineligible: () => {
            CargoV2Controller.setForm({
              actualTotalCargoGrossweigth: CargoV2Controller.form.totalAccumulationEstimationGrossWeightDataTable,
              actualTotalCargoVolumeweight: CargoV2Controller.form.totalAccumulationEstimationVolumeWeightDataTable,
              cargoActualPiece: CargoV2Controller.form.totalAccumulationActualPieceDataTable,
            });
          },
          close: () => {
            CargoV2Controller.calculateVolume(
              new CalculateVolumeApiRequestV2({
                calculateType: "INIT",
                calculateRequest: new CalculateVolumeRequestV2({
                  cargoType: this.formBooking.cargoType.value,
                  chargeableWeightReweigh: this.formBooking.cargoTotalEstimateChargeableWeight,
                  currentGrossWeight: this.formBooking.actualTotalCargoGrossweigth,
                  height: this.formBooking.actualDimension.cargoActualHeightDimension,
                  length: this.formBooking.actualDimension.cargoActualLengthDimension,
                  width: this.formBooking.actualDimension.cargoActualWidthDimension,
                  totalPieces: this.formBooking.cargoActualPiece,
                  volumeWeightReweigh: this.formBooking
                    .totalAccumulationEstimationVolumeWeightDataTable
                })
              })
            ).then(() => {
              CargoV2Controller.setForm(this.formBooking);
              if (this.formBooking.bagOrStt.length) {
                return this.navigateToEdit();
              }
              this.goBackFromSubmit();
            });
          }
        })
      )
    } catch (e) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          e,
          this.$t("cargoV2.create.searchFlight.error.networkTitle"),
          () => {
            this.onSave(flight);
          },
          () => {
            MainAppController.closeErrorMessage();
          },
        ),
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  private async _submitFromSearchFlight(flight: CargoV2SearchFlight) {
    try {
      const resp = await CargoV2Controller.submitFromSearchFlight(flight);
      this.goBackFromSubmit();
      setTimeout(() => {
        MainAppController.setSnackbarData(
          new Snackbar({
            type: "success",
            message: this.$t("cargoV2.create.snackbar.booked", {
              cargoNo: resp,
              destination: this.searchFlightParams.destinationCityCode
            }),
          }),
        );
        MainAppController.setAutoClose(true);
        MainAppController.setSnackbar(true);
      }, 1000);
    } catch (error) {
      if (!(error as any).response) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            error,
            this.$t("cargoV2.create.searchFlight.error.bookingFailed"),
            () => this.onSave(flight),
            () => {
              MainAppController.closeErrorMessage();
            },
          ),
        );
        return;
      }
      const errMessage = parsingErrorResponse(error).message;
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          imageWidth: "32",
          imageHeight: "32",
          customWidth: "417px",
          title: this.$t("cargoV2.create.modal.bookingFailed.title"),
          message: this.$t("cargoV2.create.modal.bookingFailed.message", {
            origin: this.searchFlightParams.originCityCode,
            destination: this.searchFlightParams.destinationCityCode,
            reason: errMessage
          }),
          textSuccess: this.$t("cargoV2.create.common.ok"),
          image: "circle-error",
          customButton: "w-full",
          customParentButton: "w-full",
          customWrapperButton: "w-full px-6",
          onSubmit: () => {
            CargoV2Controller.setForm(new FormCargoV2());
            MainAppController.closeMessageModal();
            this.$router.push("/cargo-v2/booking-cancelled");
          }
        }),
      );
    }
  }

  get showCalendar() {
    return FlagsPermissionCargoV2.permission_cargo_v2_search_flight_show_calendar.isEnabled();
  }

  get maxSelectableDays() {
    return FlagsPermissionCargoV2.config_search_flight_max_selectable_days.getValue();
  }
}
export default CargoV2CreateSearchFlight;
